import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { CalendarOff, FileSpreadsheet, Loader2, Pencil } from 'lucide-react';
import { api } from '../../lib/api';
import { strToDate } from '../../lib/citasApi';
import { SectionCard } from '../Common';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Mes en hora LOCAL (toISOString daría el mes anterior la madrugada del día 1).
const mesActual = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const MES_RE = /^\d{4}-\d{2}$/;
const DIAS_ABREV = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];

/** Catálogo de turnos: etiqueta, abreviatura de celda y colores. El turno
 * dice cuándo trabaja cada uno; las horas trabajadas salen de los fichajes. */
const TURNOS = {
  manana: { label: 'Mañana', abrev: 'M', cell: 'bg-sky-100 text-sky-800' },
  tarde: { label: 'Tarde', abrev: 'T', cell: 'bg-orange-100 text-orange-800' },
  completo: { label: 'Completo', abrev: 'MT', cell: 'bg-[#2E6E5E] text-white' },
  vacaciones: { label: 'Vacaciones', abrev: 'V', cell: 'bg-amber-300/60 text-amber-900' },
  libre: { label: 'Libre', abrev: 'L', cell: 'bg-gray-200 text-gray-600' },
};

/** 1560 → "26 h" · 1590 → "26,5 h" */
function fmtHoras(min) {
  const h = Math.round(((min || 0) / 60) * 10) / 10;
  return `${String(h).replace('.', ',')} h`;
}

function CupoDialog({ open, onOpenChange, worker, onSaved }) {
  const [dias, setDias] = useState(22);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && worker) setDias(worker.vacaciones_anuales ?? 22);
  }, [open, worker]);

  const guardar = async () => {
    setSaving(true);
    try {
      await api.put(`/api/cuadrante/vacaciones/${worker.user_id}`, { dias: Number(dias) });
      toast.success(`Cupo de vacaciones de ${worker.user_name}: ${dias} días/año`);
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo guardar el cupo');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xs" data-testid="cuadrante-cupo-dialog">
        <DialogHeader>
          <DialogTitle>Vacaciones de {worker?.user_name}</DialogTitle>
        </DialogHeader>
        <div>
          <Label>Días de vacaciones al año</Label>
          <Input
            type="number"
            min={0}
            max={90}
            value={dias}
            onChange={(e) => setDias(e.target.value)}
            className="mt-1.5"
            data-testid="cuadrante-cupo-dias"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button
            onClick={guardar}
            disabled={saving}
            className="bg-[#2E6E5E] hover:bg-[#1E4A3F]"
            data-testid="cuadrante-cupo-guardar"
          >
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Cuadrante de turnos (Phase 27.19) — rejilla trabajadores × días del mes.
 *
 * Admin: elige un "pincel" (mañana/tarde/completo/vacaciones/libre) y pinta
 * celdas con un clic; repetir el clic con el mismo turno la borra. El resto
 * del personal ve SOLO su fila y su resumen (horas, vacaciones restantes).
 */
export default function CuadranteTab({ user, admin }) {
  const [month, setMonth] = useState(mesActual());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [brush, setBrush] = useState('manana');
  const [saving, setSaving] = useState(new Set()); // claves user_id|date en vuelo
  const [cupoWorker, setCupoWorker] = useState(null);
  const [workerFilter, setWorkerFilter] = useState('todos'); // solo admins
  // Refs para la recarga diferida tras pintar: el timer no puede vivir en una
  // función (se recrea en cada render) y las respuestas tardías de otro mes
  // no deben machacar la vista.
  const reloadTimer = useRef(null);
  const monthRef = useRef(month);
  monthRef.current = month;

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/cuadrante', { params: { month } });
      if (res.data?.month === monthRef.current) setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo cargar el cuadrante');
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => {
    cargar();
    return () => clearTimeout(reloadTimer.current);
  }, [cargar]);

  // (user_id, date) → turno
  const celdas = useMemo(() => {
    const map = {};
    for (const t of data?.turnos || []) map[`${t.user_id}|${t.date}`] = t.turno;
    return map;
  }, [data]);

  const pintar = async (workerId, date) => {
    if (!admin) return;
    const key = `${workerId}|${date}`;
    if (saving.has(key)) return;
    const actual = celdas[key];
    const nuevo = actual === brush ? null : brush; // mismo pincel = borrar
    setSaving((s) => new Set(s).add(key));
    try {
      await api.put('/api/cuadrante', { user_id: workerId, date, turno: nuevo });
      // Actualización local sin recargar todo: solo turnos; los totales se
      // refrescan con una recarga silenciosa al soltar la ráfaga de clics.
      setData((d) => ({
        ...d,
        turnos: [
          ...d.turnos.filter((t) => !(t.user_id === workerId && t.date === date)),
          ...(nuevo ? [{ user_id: workerId, date, turno: nuevo }] : []),
        ],
      }));
      clearTimeout(reloadTimer.current);
      reloadTimer.current = setTimeout(async () => {
        try {
          const res = await api.get('/api/cuadrante', { params: { month: monthRef.current } });
          // Descartar respuestas obsoletas (el usuario cambió de mes mientras tanto).
          if (res.data?.month === monthRef.current) setData(res.data);
        } catch { /* la vista local sigue siendo válida */ }
      }, 800);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo guardar el turno');
    } finally {
      setSaving((s) => { const n = new Set(s); n.delete(key); return n; });
    }
  };

  const exportar = () => {
    fetch(`${BACKEND_URL}/api/cuadrante/export?month=${month}`, { credentials: 'include' })
      .then((r) => { if (!r.ok) throw new Error('export'); return r.blob(); })
      .then((blob) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `cuadrante_${month}.xlsx`;
        a.click();
      })
      .catch(() => toast.error('No se pudo exportar el cuadrante'));
  };

  const days = data?.days || [];
  const workers = data?.workers || [];

  // Admin: el desplegable elige de quién es el resumen y filtra la rejilla.
  // El resto del personal siempre se ve a sí mismo.
  const resumenId = admin && workerFilter !== 'todos' ? workerFilter : user?.id;
  const resumenWorker = workers.find((w) => w.user_id === resumenId);
  const miTotal = data?.totales?.[resumenId];
  const tituloResumen = resumenId === user?.id
    ? `Mi resumen — ${month}`
    : `Resumen de ${resumenWorker?.user_name || ''} — ${month}`;
  const workersVisibles = admin && workerFilter !== 'todos'
    ? workers.filter((w) => w.user_id === workerFilter)
    : workers;

  return (
    <div className="space-y-6">
      {/* Resumen personal — para todos (al admin también le sirve el suyo) */}
      {miTotal && (
        <SectionCard title={tituloResumen}>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="cuadrante-mi-resumen">
            <div className="rounded-lg border border-[#D9E3DD] p-3">
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Horas trabajadas</div>
              <div className="text-xl font-bold text-[#13231E]">{fmtHoras(miTotal.fichadas_min)}</div>
              <div className="text-[11px] text-muted-foreground">fichadas este mes (tramos cerrados)</div>
            </div>
            <div className="rounded-lg border border-[#D9E3DD] p-3">
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Mis turnos del mes</div>
              <div className="text-xl font-bold text-[#13231E]">
                {miTotal.turnos_manana + miTotal.turnos_tarde + miTotal.turnos_completo}
              </div>
              <div className="text-[11px] text-muted-foreground">
                {miTotal.turnos_manana} mañanas · {miTotal.turnos_tarde} tardes · {miTotal.turnos_completo} completos
              </div>
            </div>
            <div className="rounded-lg border border-[#D9E3DD] p-3">
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Vacaciones pedidas</div>
              <div className="text-xl font-bold text-[#13231E]">
                {miTotal.vacaciones_ano} <span className="text-sm font-normal">días este año</span>
              </div>
              <div className="text-[11px] text-muted-foreground">{miTotal.vacaciones_mes} este mes</div>
            </div>
            <div className="rounded-lg border border-[#D9E3DD] p-3 bg-amber-50/60">
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Vacaciones restantes</div>
              <div className="text-xl font-bold text-amber-700" data-testid="cuadrante-vac-restantes">
                {miTotal.vacaciones_restantes} <span className="text-sm font-normal">de {miTotal.vacaciones_cupo}</span>
              </div>
              <div className="text-[11px] text-muted-foreground">cupo anual</div>
            </div>
          </div>
        </SectionCard>
      )}

      <SectionCard
        title={admin ? 'Cuadrante del equipo' : 'Mis turnos'}
        action={(
          <div className="flex flex-wrap items-center gap-2">
            {admin && (
              <Select value={workerFilter} onValueChange={setWorkerFilter}>
                <SelectTrigger className="h-9 min-w-[180px]" data-testid="cuadrante-worker-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todo el equipo</SelectItem>
                  {workers.map((w) => (
                    <SelectItem key={w.user_id} value={w.user_id}>{w.user_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Input
              type="month"
              value={month}
              onChange={(e) => MES_RE.test(e.target.value) && setMonth(e.target.value)}
              className="h-9 w-40"
              data-testid="cuadrante-month"
            />
            {admin && (
              <Button onClick={exportar} variant="outline" className="h-9" data-testid="cuadrante-export">
                <FileSpreadsheet className="w-4 h-4 mr-1.5 text-[#2E6E5E]" /> Excel
              </Button>
            )}
          </div>
        )}
      >
        {/* Pincel de turnos (admin) / leyenda (todos) */}
        <div className="flex flex-wrap items-center gap-1.5 mb-3" data-testid="cuadrante-pincel">
          {admin && <span className="text-xs text-muted-foreground mr-1">Pintar con:</span>}
          {Object.entries(TURNOS).map(([key, t]) => (
            <button
              key={key}
              type="button"
              onClick={() => admin && setBrush(key)}
              disabled={!admin}
              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
                admin && brush === key ? 'border-[#2E6E5E] ring-2 ring-[#2E6E5E]/30' : 'border-[#D9E3DD]'
              } ${admin ? 'cursor-pointer' : 'cursor-default'}`}
              data-testid={`cuadrante-pincel-${key}`}
            >
              <span className={`inline-flex w-6 h-5 items-center justify-center rounded text-[10px] font-bold ${t.cell}`}>
                {t.abrev}
              </span>
              {t.label}
            </button>
          ))}
          {admin && (
            <span className="text-[11px] text-muted-foreground ml-1">
              Clic en una celda para pintar; repetir el clic la borra.
            </span>
          )}
        </div>

        {loading ? (
          <div className="py-8 text-center text-sm text-muted-foreground">Cargando cuadrante…</div>
        ) : workers.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">
            <CalendarOff className="w-8 h-8 text-[#2E6E5E] mx-auto mb-2" />
            Sin trabajadores.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="text-sm border-collapse" data-testid="cuadrante-tabla">
              <thead>
                <tr className="text-[11px] uppercase tracking-wider text-muted-foreground">
                  <th className="sticky left-0 bg-white py-2 pr-3 text-left min-w-[150px] border-b border-border">
                    Trabajador
                  </th>
                  {days.map((d) => {
                    const wd = (strToDate(d).getDay() + 6) % 7;
                    const finde = wd >= 5;
                    return (
                      <th
                        key={d}
                        className={`py-1 px-0.5 text-center font-medium border-b border-border ${finde ? 'bg-[#F0F6F2]' : ''}`}
                      >
                        <div>{DIAS_ABREV[wd]}</div>
                        <div className="tabular-nums">{Number(d.slice(8))}</div>
                      </th>
                    );
                  })}
                  <th className="py-2 pl-3 pr-2 text-right border-b border-border whitespace-nowrap" title="Horas trabajadas (fichadas) este mes">
                    Horas
                  </th>
                  <th className="py-2 px-2 text-right border-b border-border whitespace-nowrap" title="Vacaciones pedidas este año / restantes">
                    Vac. año
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {workersVisibles.map((w) => {
                  const tot = data?.totales?.[w.user_id] || {};
                  return (
                    <tr key={w.user_id} data-testid={`cuadrante-fila-${w.user_id}`}>
                      <td className="sticky left-0 bg-white py-1.5 pr-3 font-medium text-[#13231E] whitespace-nowrap">
                        {w.user_name}
                        {admin && (
                          <button
                            type="button"
                            className="ml-1.5 text-muted-foreground hover:text-[#13231E] align-middle"
                            title={`Cupo de vacaciones: ${w.vacaciones_anuales} días/año`}
                            onClick={() => setCupoWorker(w)}
                            data-testid={`cuadrante-cupo-${w.user_id}`}
                          >
                            <Pencil className="w-3 h-3 inline" />
                          </button>
                        )}
                      </td>
                      {days.map((d) => {
                        const key = `${w.user_id}|${d}`;
                        const turno = celdas[key];
                        const wd = (strToDate(d).getDay() + 6) % 7;
                        const finde = wd >= 5;
                        return (
                          <td key={d} className={`p-0.5 text-center ${finde ? 'bg-[#F0F6F2]' : ''}`}>
                            <button
                              type="button"
                              onClick={() => pintar(w.user_id, d)}
                              disabled={!admin || saving.has(key)}
                              className={`w-7 h-7 rounded text-[10px] font-bold transition-colors ${
                                turno
                                  ? TURNOS[turno]?.cell || ''
                                  : admin
                                    ? 'hover:bg-[#2E6E5E]/15 text-transparent hover:text-[#2E6E5E]'
                                    : 'text-transparent'
                              } ${admin ? 'cursor-pointer' : 'cursor-default'}`}
                              data-testid={`cuadrante-celda-${w.user_id}-${d}`}
                            >
                              {turno ? TURNOS[turno]?.abrev : '·'}
                            </button>
                          </td>
                        );
                      })}
                      <td className="py-1.5 pl-3 pr-2 text-right tabular-nums whitespace-nowrap">
                        {fmtHoras(tot.fichadas_min)}
                      </td>
                      <td className="py-1.5 px-2 text-right tabular-nums whitespace-nowrap">
                        {tot.vacaciones_ano ?? 0}
                        <span className="text-muted-foreground"> / quedan {tot.vacaciones_restantes ?? '—'}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      <CupoDialog
        open={!!cupoWorker}
        onOpenChange={(v) => !v && setCupoWorker(null)}
        worker={cupoWorker}
        onSaved={cargar}
      />
    </div>
  );
}
